import { gql, useQuery } from '@apollo/client';
import algoliasearch, { SearchClient } from 'algoliasearch';
import * as React from 'react';
import Cookies from 'universal-cookie';

import { useCurrent } from '@/hooks/useCurrent';

import { UseCommunityUsers_GetCommunityUsersSearchKeys } from './graphql-types/UseCommunityUsers_GetCommunityUsersSearchKeys';
import { TenantSearchMode } from '../../components/search/utils';

export const GET_SEARCH_KEYS = gql`
  query UseCommunityUsers_GetCommunityUsersSearchKeys {
    getCommunityUsersSearchKeys {
      applicationId
      searchApiKey
      indexData {
        memberIndexName
        searchableAttributesField
        viewableAttributesField
      }
    }
  }
`;

const cookie = new Cookies();

type IInstantSearchQuery = {
  indexName: string;
  params?: {
    query?: string;
    page?: number;
    hitsPerPage?: number;
    filters?: string;
    attributesToRetrieve?: string[] | string;
    attributesToHighlight?: string[] | string;
    attributesToSnippet?: string[] | string;
    restrictSearchableAttributes?: string[] | string;
  };
};

type IInstantSearchClientProxy = SearchClient & {
  clearCache: () => Promise<void>;
  search: (
    queries: readonly IInstantSearchQuery[],
    requestOptions?: unknown
  ) => Promise<{ results: Array<Record<string, unknown>> }>;
  initIndex: (indexName: string) => {
    search: (
      searchText: string,
      options?: {
        hitsPerPage?: number;
        page?: number;
        filters?: string;
        attributesToRetrieve?: string[] | string;
      }
    ) => Promise<Record<string, unknown>>;
  };
};

const normalizeStringArray = (
  value?: string[] | string
): string[] | undefined => {
  if (!value) return undefined;
  return Array.isArray(value)
    ? value
    : value
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);
};

const parseCommunityFilters = (
  filters?: string
): Array<Record<string, unknown>> => {
  if (!filters?.trim()) return [];
  return filters
    .split(/\s+AND\s+/)
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => {
      const notMatch = part.match(/^NOT\s+([A-Za-z0-9._-]+)\s*:\s*(.+)$/);
      if (notMatch) {
        const [, field, raw] = notMatch;
        return { bool: { must_not: [{ term: { [field]: raw.trim() } }] } };
      }
      const match = part.match(/^([A-Za-z0-9._-]+)\s*:\s*(.+)$/);
      if (!match) return null;
      const [, field, raw] = match;
      return { term: { [field]: raw.trim() } };
    })
    .filter(Boolean) as Array<Record<string, unknown>>;
};

export const useCommunityUsers = (
  skip?: boolean
): {
  loading: boolean;
  indexName?: string;
  searchClient: SearchClient | null;
  viewableAttributesField?: string;
  searchableAttributesField?: string;
} => {
  const { loading: searchKeysLoading, data } =
    useQuery<UseCommunityUsers_GetCommunityUsersSearchKeys>(GET_SEARCH_KEYS, {
      skip,
    });
  const current = useCurrent();

  const searchClient = React.useMemo(() => {
    const currentApplicationId =
      data?.getCommunityUsersSearchKeys.applicationId;
    const currentSearchApiKey = data?.getCommunityUsersSearchKeys.searchApiKey;

    if (!currentApplicationId || !currentSearchApiKey) {
      return null;
    }

    const tenantSearchMode = (
      current?.tenant as { searchMode?: unknown } | null
    )?.searchMode;
    if (tenantSearchMode !== TenantSearchMode.AI) {
      return algoliasearch(currentApplicationId, currentSearchApiKey);
    }

    const aiSearchBaseUrl =
      process.env.NEXT_PUBLIC_AI_SEARCH_BASE_URL ?? '/api/v1/ai-search';
    const normalizedBaseUrl = aiSearchBaseUrl.replace(/\/$/, '');
    const queryCache = new Map<
      string,
      { results: Array<Record<string, unknown>> }
    >();
    const emptyResultsForQueries = (queries: IInstantSearchQuery[]) => ({
      results: queries.map((q) => ({
        hits: [],
        nbHits: 0,
        nbPages: 0,
        page: 0,
        processingTimeMS: 0,
        hitsPerPage: q.params?.hitsPerPage ?? 20,
        exhaustiveNbHits: false,
        query: q.params?.query ?? '',
        params: '',
        index: q.indexName,
      })),
    });

    const aiProxyClient: IInstantSearchClientProxy = {
      async search(queriesInput: readonly IInstantSearchQuery[]) {
        const queries = queriesInput as IInstantSearchQuery[];
        const cacheKey = JSON.stringify({ queries });
        if (queryCache.has(cacheKey)) return queryCache.get(cacheKey)!;

        const payload = {
          requests: queries.map((queryItem) => {
            const params = queryItem.params ?? {};
            return {
              indexName: queryItem.indexName,
              scope: 'community',
              query: params.query ?? '',
              hitsPerPage: params.hitsPerPage ?? 20,
              page: params.page ?? 0,
              queryFilter: parseCommunityFilters(params.filters),
              attributesToRetrieve: normalizeStringArray(
                params.attributesToRetrieve
              ),
              attributesToHighlight: normalizeStringArray(
                params.attributesToHighlight
              ) || ['displayName'],
              attributesToSnippet: normalizeStringArray(
                params.attributesToSnippet
              ) || ['*'],
              restrictSearchableAttributes: normalizeStringArray(
                params.restrictSearchableAttributes
              ),
            };
          }),
        };

        const requestId = crypto.randomUUID();
        const response = await fetch(
          `${normalizedBaseUrl}/1/indexes/*/queries`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'x-request-id': requestId,
              'x-user-id': current.user?._id ?? '',
              'x-tenant-id': current.tenant?._id ?? '',
              ...(cookie.get('accounts%3ArefreshToken')
                ? { 'x-refresh-token': cookie.get('accounts%3ArefreshToken') }
                : {}),
              Authorization: `Bearer ${cookie.get('accounts%3AaccessToken')}`,
            },
            body: JSON.stringify(payload),
          }
        );

        if (!response.ok) {
          const mapped = emptyResultsForQueries(queries);
          queryCache.set(cacheKey, mapped);
          return mapped;
        }

        const json = (await response.json()) as {
          results?: Array<Record<string, unknown>>;
        };
        const rawResults = json.results ?? [];
        const mapped = {
          results: queries.map((q, i) => ({
            ...(rawResults[i] ?? emptyResultsForQueries([q]).results[0]),
            index: q.indexName,
          })),
        };
        queryCache.set(cacheKey, mapped);
        return mapped;
      },
      async clearCache() {
        queryCache.clear();
      },
      initIndex(indexName: string) {
        return {
          search: async (
            searchText: string,
            options?: {
              hitsPerPage?: number;
              page?: number;
              filters?: string;
              attributesToRetrieve?: string[] | string;
            }
          ) => {
            const singleResult = await aiProxyClient.search([
              {
                indexName,
                params: {
                  query: searchText,
                  hitsPerPage: options?.hitsPerPage ?? 20,
                  page: options?.page ?? 0,
                  filters: options?.filters,
                  attributesToRetrieve: options?.attributesToRetrieve,
                },
              },
            ]);

            return (singleResult.results?.[0] ?? {
              hits: [],
              nbHits: 0,
              nbPages: 0,
              page: 0,
              processingTimeMS: 0,
              hitsPerPage: options?.hitsPerPage ?? 20,
              exhaustiveNbHits: false,
              query: searchText,
              params: '',
              index: indexName,
            }) as Record<string, unknown>;
          },
        };
      },
    } as unknown as IInstantSearchClientProxy;

    return aiProxyClient;
  }, [
    data?.getCommunityUsersSearchKeys.applicationId,
    data?.getCommunityUsersSearchKeys.searchApiKey,
    current?.tenant,
    current?.user?._id,
  ]);

  if (!!skip) {
    return {
      loading: false,
      indexName: undefined,
      searchClient: null,
    };
  }
  if (searchKeysLoading || current.loading || !searchClient) {
    return {
      loading: true,
      indexName: undefined,
      searchClient: null,
    };
  }

  if (!current.tenant?.associationCode) {
    throw new Error('No association code.');
  }

  if (!data) {
    throw new Error('No search keys.');
  }

  return {
    loading: false,
    indexName: data.getCommunityUsersSearchKeys.indexData.memberIndexName,
    searchClient,
    viewableAttributesField:
      data.getCommunityUsersSearchKeys.indexData.viewableAttributesField,
    searchableAttributesField:
      data.getCommunityUsersSearchKeys.indexData.searchableAttributesField,
  };
};
